"use client"

import React from "react"
import { useRouter } from "next/router"

import { useOrganization } from "@clerk/nextjs"
import {
  Breadcrumb,
  BREADCRUMB_TOOLTIP_CLASS_NAME,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
  TruncatedText,
} from "@coval/ui"

import { TruncatedBreadcrumbPage } from "@/components/resource-header/TruncatedBreadcrumbPage"
import { useHeaderActions } from "@/contexts/HeaderActionsContext"
import {
  formatBreadcrumb,
  isOmittedBreadcrumbSegment,
} from "@/utils/breadcrumbLabels"
import { parseWorkspaceRoute } from "@/utils/workspaceRoutes"

import { HeaderActionControls } from "./HeaderActionControls"

interface DefaultPageHeaderProps {
  getHref: (baseHref: string) => string
}

interface BreadcrumbEntry {
  key: string
  node: React.ReactNode
}

export const DefaultPageHeader = ({ getHref }: DefaultPageHeaderProps) => {
  const router = useRouter()
  const { organization } = useOrganization()
  const { headerAction } = useHeaderActions()
  const responsiveActions =
    headerAction?.type === "custom" && headerAction.responsive

  const getBreadcrumbs = () => {
    const parsed = parseWorkspaceRoute(router.asPath)
    if (parsed) {
      return parsed.relativePath.split("/").filter(Boolean)
    }
    const pathname = router.asPath.split("?")[0]
    const segments = pathname
      .split("/")
      .filter((segment) => segment && segment !== organization?.slug)
    return segments.map((segment) => segment.split("#")[0])
  }

  const buildPageBreadcrumbs = (): BreadcrumbEntry[] => {
    const segments = getBreadcrumbs()
    const items: BreadcrumbEntry[] = []

    segments.forEach((segment, index) => {
      if (isOmittedBreadcrumbSegment(segment, index, segments.length)) {
        return
      }

      const arr = segments
      const isLast = index === arr.length - 1

      const path = `/${arr.slice(0, index + 1).join("/")}`
      items.push({
        key: `${segment}-${index}`,
        node: isLast ? (
          <TruncatedBreadcrumbPage>
            {formatBreadcrumb(segment, index)}
          </TruncatedBreadcrumbPage>
        ) : (
          <TruncatedText
            asChild
            tooltipClassName={BREADCRUMB_TOOLTIP_CLASS_NAME}
          >
            <BreadcrumbLink href={getHref(path)}>
              {formatBreadcrumb(segment, index)}
            </BreadcrumbLink>
          </TruncatedText>
        ),
      })
    })

    return items
  }

  const breadcrumbEntries = buildPageBreadcrumbs()

  return (
    <div className="flex flex-1 min-w-0 items-center justify-between gap-2">
      <div
        className={`flex justify-start gap-2 items-center min-w-0 ${responsiveActions ? "flex-[0_1_auto]" : "flex-1"}`}
      >
        <Breadcrumb className="min-w-0 max-w-full">
          <BreadcrumbList className="flex-nowrap whitespace-nowrap">
            {breadcrumbEntries.map((entry, i) => (
              <React.Fragment key={entry.key}>
                {i > 0 && <BreadcrumbSeparator className="shrink-0" />}
                <BreadcrumbItem className="min-w-0">
                  {entry.node}
                </BreadcrumbItem>
              </React.Fragment>
            ))}
          </BreadcrumbList>
        </Breadcrumb>
      </div>
      <div
        className={`flex min-w-0 items-center gap-2 ${responsiveActions ? "flex-1" : "shrink-0"}`}
      >
        <HeaderActionControls headerAction={headerAction} />
      </div>
    </div>
  )
}
